/**
 * Ticket 114.8 — PLATFORM_ADMIN catalog management page.
 *
 * Provides:
 *   - Tabs for Countries, Industries, Stats, and Pending-refresh queue.
 *   - "Run once" button per industry that calls the stat-refresh endpoint.
 *   - Pending-refresh queue: lists rows with diff (old → new) and
 *     Approve / Reject buttons.
 *   - Config panel: read-only display of stat-refresh env-time config.
 *
 * Route: /admin/demos/catalog
 * Required permission: demo.catalog.manage (PLATFORM_ADMIN role).
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
  listDemoCountries,
  listDemoIndustries,
  runStatRefreshOnce,
  listPendingStatRefreshes,
  approveStatRefresh,
  rejectStatRefresh,
  getStatRefreshConfig,
  type DemoCountry,
  type DemoIndustry,
  type RefreshResult,
  type StatRefreshPendingRow,
  type StatRefreshConfig,
} from '@/api/demos';

type Tab = 'countries' | 'industries' | 'stats' | 'pendingRefresh';

// ------------------------------------------------------------------ //
// Countries tab
// ------------------------------------------------------------------ //

function CountriesTab() {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<DemoCountry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listDemoCountries()
      .then(setCountries)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground">{t('demos.catalog.countries.loadError')}</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('demos.admin.catalog.tabs.countries')}</h2>
      </div>
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.code')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.name')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.currency')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.language')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.isActive')}</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((c) => (
              <tr key={c.code} className="border-t border-border">
                <td className="px-3 py-2 font-mono">{c.code}</td>
                <td className="px-3 py-2">{c.name}</td>
                <td className="px-3 py-2">{c.defaultCurrency}</td>
                <td className="px-3 py-2">{c.defaultLanguage}</td>
                <td className="px-3 py-2">{c.isActive ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Industries + Run Once tab
// ------------------------------------------------------------------ //

interface IndustriesTabProps {
  onRefreshResult: (msg: string) => void;
}

function IndustriesTab({ onRefreshResult }: IndustriesTabProps) {
  const { t } = useTranslation();
  const [industries, setIndustries] = useState<DemoIndustry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listDemoIndustries()
      .then(setIndustries)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleRunOnce(country: string, key: string) {
    const runKey = `${country}/${key}`;
    setRefreshing(runKey);
    try {
      const result: RefreshResult = await runStatRefreshOnce(country, key);
      if (result.errorMessage) {
        onRefreshResult(`${t('demos.admin.catalog.stats.refreshError')}: ${result.errorMessage}`);
      } else {
        onRefreshResult(
          `${t('demos.admin.catalog.stats.refreshSuccess')} (+${result.added} added, ~${result.updated} updated, ${result.skipped} skipped, ${result.pending} pending)`,
        );
      }
    } catch {
      onRefreshResult(t('demos.admin.catalog.stats.refreshError'));
    } finally {
      setRefreshing(null);
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">{t('demos.catalog.industries.loadError')}</p>;

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">{t('demos.admin.catalog.tabs.industries')}</h2>
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.countries.code')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.industries.key')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.industries.name')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.stats.refresh')}</th>
            </tr>
          </thead>
          <tbody>
            {industries.map((ind) => {
              const runKey = `${ind.country}/${ind.key}`;
              return (
                <tr key={runKey} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{ind.country}</td>
                  <td className="px-3 py-2 font-mono">{ind.key}</td>
                  <td className="px-3 py-2">{ind.name}</td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={refreshing === runKey}
                      onClick={() => handleRunOnce(ind.country, ind.key)}
                      aria-label={t('demos.admin.catalog.stats.runOnce')}
                    >
                      {refreshing === runKey ? '...' : t('demos.admin.catalog.stats.runOnce')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Pending refresh queue tab
// ------------------------------------------------------------------ //

interface PendingRefreshTabProps {
  onAction: (msg: string) => void;
}

function PendingRefreshTab({ onAction }: PendingRefreshTabProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<StatRefreshPendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listPendingStatRefreshes()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleApprove(id: string) {
    setActionInFlight(id);
    try {
      await approveStatRefresh(id);
      onAction(t('demos.admin.catalog.pending.approved'));
      load();
    } catch {
      onAction(t('demos.admin.catalog.stats.refreshError'));
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleReject(id: string) {
    setActionInFlight(id);
    try {
      await rejectStatRefresh(id);
      onAction(t('demos.admin.catalog.pending.rejected'));
      load();
    } catch {
      onAction(t('demos.admin.catalog.stats.refreshError'));
    } finally {
      setActionInFlight(null);
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('demos.admin.catalog.pending.empty')}</p>;
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">{t('demos.admin.catalog.pending.title')}</h2>
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.pending.country')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.pending.industry')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.pending.statKey')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.pending.currentValue')}</th>
              <th className="px-3 py-2 text-left">{t('demos.admin.catalog.pending.proposedValue')}</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono">{row.country}</td>
                <td className="px-3 py-2 font-mono">{row.industryKey}</td>
                <td className="px-3 py-2 font-mono">{row.statKey}</td>
                <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                  {row.currentValue ?? '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-[color:var(--color-lop-teal)]">
                  {row.proposedValue}
                </td>
                <td className="px-3 py-2 flex gap-2">
                  <Button
                    size="sm"
                    disabled={actionInFlight === row.id}
                    onClick={() => handleApprove(row.id)}
                    aria-label={t('demos.admin.catalog.pending.approve')}
                  >
                    {t('demos.admin.catalog.pending.approve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionInFlight === row.id}
                    onClick={() => handleReject(row.id)}
                    aria-label={t('demos.admin.catalog.pending.reject')}
                  >
                    {t('demos.admin.catalog.pending.reject')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Config panel (read-only)
// ------------------------------------------------------------------ //

function ConfigPanel() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<StatRefreshConfig | null>(null);

  useEffect(() => {
    getStatRefreshConfig().then(setConfig).catch(() => {});
  }, []);

  if (!config) return null;

  return (
    <div className="rounded-md border border-border p-4 space-y-2 text-sm">
      <p className="font-semibold text-muted-foreground uppercase tracking-wide text-xs">
        Config (env-time, read-only)
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">{t('demos.admin.catalog.config.statRefreshEnabled')}</dt>
        <dd>{config.enabled ? 'Yes' : 'No'}</dd>
        <dt className="text-muted-foreground">{t('demos.admin.catalog.config.requireApproval')}</dt>
        <dd>{config.requireApproval ? 'Yes' : 'No'}</dd>
        <dt className="text-muted-foreground">{t('demos.admin.catalog.config.webSearchEnabled')}</dt>
        <dd>{config.webSearchEnabled ? 'Yes' : 'No'}</dd>
      </dl>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Main page
// ------------------------------------------------------------------ //

export function DemoCatalogAdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('countries');
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  const TABS: { key: Tab; label: string }[] = [
    { key: 'countries', label: t('demos.admin.catalog.tabs.countries') },
    { key: 'industries', label: t('demos.admin.catalog.tabs.industries') },
    { key: 'pendingRefresh', label: t('demos.admin.catalog.tabs.pendingRefresh') },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 right-4 z-50 bg-background border border-border rounded-md px-4 py-2 text-sm shadow-lg"
        >
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('demos.admin.catalog.title')}</h1>
      </div>

      <ConfigPanel />

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors
              ${tab === key
                ? 'border-[color:var(--color-lop-teal)] text-[color:var(--color-lop-teal)]'
                : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'countries' && <CountriesTab />}
      {tab === 'industries' && <IndustriesTab onRefreshResult={showToast} />}
      {tab === 'pendingRefresh' && <PendingRefreshTab onAction={showToast} />}
    </div>
  );
}
